// "Scout at the time": the snapshot the Launcher sends with a report. First the
// few facts a report is usually about (versions, battery, position, network,
// licence), then every group on "All details", and the raw JSON under that.
// The snapshot is whatever that Launcher version sent; older reports carry
// `{}`. Every value is checked for its type before it is shown, and a row or
// group with nothing in it is left out.

import { useState, type ReactNode } from 'react';
import { ArrowTopRightOnSquareIcon, CheckCircleIcon, ChevronDownIcon, MinusCircleIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { formatDateTime } from '../../utils/formatters';

type Obj = Record<string, unknown>;

function asObj(v: unknown): Obj | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Obj) : null;
}
function num(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
}
function str(v: unknown): string | null {
  if (typeof v === 'string') return v.trim() || null;
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return null;
}
function bool(v: unknown): boolean | null {
  return typeof v === 'boolean' ? v : null;
}
function fmt(n: number, digits = 1): string {
  return n.toLocaleString('en-NZ', { maximumFractionDigits: digits });
}
function megabytes(mb: number): string {
  return mb >= 1024 ? `${fmt(mb / 1024)} GB` : `${fmt(mb, 0)} MB`;
}
function hours(h: number): string {
  if (h < 1) return `${fmt(h * 60, 0)} minutes`;
  if (h < 48) return `${fmt(h)} hours`;
  return `${fmt(h / 24)} days`;
}

/** A yes/no as a glyph and a word, never colour alone. */
function YesNo({ value, yes, no, warnWhenNo = false }: { value: boolean; yes: string; no: string; warnWhenNo?: boolean }) {
  return value ? (
    <span className="inline-flex items-center gap-1 text-green-300">
      <CheckCircleIcon className="h-4 w-4" aria-hidden="true" />{yes}
    </span>
  ) : (
    <span className={clsx('inline-flex items-center gap-1', warnWhenNo ? 'text-amber-300' : 'text-slate-300')}>
      <MinusCircleIcon className="h-4 w-4" aria-hidden="true" />{no}
    </span>
  );
}

type Row = [label: string, value: ReactNode | null];

const present = (rows: Row[]) => rows.filter(([, v]) => v !== null && v !== undefined && v !== '');

function Rows({ rows }: { rows: Row[] }) {
  return (
    <dl className="grid grid-cols-[minmax(0,auto)_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-sm">
      {rows.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="text-slate-400">{label}</dt>
          <dd className="min-w-0 break-words text-right tabular-nums text-slate-100">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function Group({ title, rows, children }: { title: string; rows: Row[]; children?: ReactNode }) {
  const shown = present(rows);
  if (!shown.length && !children) return null;
  return (
    <section aria-label={title}>
      <h4 className="mb-1.5 text-[13px] font-semibold uppercase tracking-wider text-slate-500">{title}</h4>
      {shown.length > 0 && <Rows rows={shown} />}
      {children}
    </section>
  );
}

/** pkg → version, from an object, an array of pairs/objects/strings, or nothing. */
export function appVersionList(v: unknown): { name: string; version: string }[] {
  const o = asObj(v);
  if (o) {
    return Object.entries(o)
      .map(([name, ver]) => ({ name, version: str(ver) ?? '' }))
      .filter((a) => a.name);
  }
  if (Array.isArray(v)) {
    return v.flatMap((item) => {
      if (typeof item === 'string') {
        const [name, ...rest] = item.split(/[:=@ ]/);
        return name ? [{ name, version: rest.join(' ').trim() }] : [];
      }
      if (Array.isArray(item) && typeof item[0] === 'string') return [{ name: item[0], version: str(item[1]) ?? '' }];
      const io = asObj(item);
      const name = io && (str(io.package) ?? str(io.pkg) ?? str(io.name));
      return name ? [{ name, version: str(io!.version) ?? str(io!.version_name) ?? '' }] : [];
    });
  }
  return [];
}

export function mapLink(lat: number, lon: number): string {
  return `https://www.google.com/maps/search/?api=1&query=${lat},${lon}`;
}

export function FeedbackSystemInfo({
  info,
  fallbackPosition,
  className,
}: {
  info: unknown;
  /** The report's own position, used when the snapshot has none. */
  fallbackPosition?: { lat: number | null; lon: number | null };
  /** The section's own frame, from the page it sits in. */
  className?: string;
}) {
  const [all, setAll] = useState(false);
  const [raw, setRaw] = useState(false);
  const si = asObj(info) ?? {};
  const battery = asObj(si.battery) ?? {};
  const wifi = asObj(si.wifi) ?? {};
  const gps = asObj(si.gps) ?? {};
  const cell = asObj(si.cellular) ?? {};
  const bt = asObj(si.bluetooth) ?? {};
  const la = asObj(si.la_status) ?? {};
  const apps = appVersionList(si.app_versions);

  const pct = num(battery.percent);
  const charging = bool(battery.charging);
  const temp = num(battery.temp_c);
  const health = str(battery.health);

  const wifiOn = bool(wifi.connected);
  const rssi = num(wifi.rssi);

  const fix = bool(gps.has_fix);
  const sats = num(gps.satellites_used);
  const acc = num(gps.accuracy_m);
  const lat = num(gps.lat) ?? fallbackPosition?.lat ?? null;
  const lon = num(gps.lon) ?? fallbackPosition?.lon ?? null;
  const hasPos = lat !== null && lon !== null && !(lat === 0 && lon === 0);

  const sentAt = str(si.timestamp);
  const sentDate = sentAt && !Number.isNaN(new Date(sentAt).getTime()) ? new Date(sentAt) : null;
  const nmea = bool(si.nmea_connected);
  const freeMem = num(si.free_memory_mb);
  const freeDisk = num(si.storage_free_mb);
  const uptime = num(si.uptime_hours);

  const groups = [
    <Group key="scout" title="Scout" rows={[
      ['Model', str(si.device_model)],
      ['Android', str(si.android_version)],
      ['Launcher', str(si.launcher_version)],
      ['Screen', str(si.screen)],
      ['Time zone', str(si.timezone)],
      ['Snapshot', sentDate ? formatDateTime(sentDate) : null],
    ]} />,
    <Group key="battery" title="Battery" rows={[
      ['Charge', pct !== null ? (
        <span className={pct < 15 && charging !== true ? 'text-red-300' : pct < 30 ? 'text-amber-300' : undefined}>{fmt(pct, 0)}%</span>
      ) : null],
      ['Charging', charging !== null ? <YesNo value={charging} yes="Yes" no="No" /> : null],
      ['Health', health],
      ['Temperature', temp !== null ? `${fmt(temp)} °C` : null],
    ]} />,
    <Group key="wifi" title="Wi-Fi" rows={[
      ['Connection', wifiOn !== null ? <YesNo value={wifiOn} yes="Connected" no="Not connected" warnWhenNo /> : null],
      ['Network', str(wifi.ssid)],
      ['Signal', rssi !== null ? `${fmt(rssi, 0)} dBm` : null],
      ['IP address', str(wifi.ip)],
    ]} />,
    <Group key="gps" title="GPS" rows={[
      ['Fix', fix !== null ? <YesNo value={fix} yes="Has a fix" no="No fix" warnWhenNo /> : null],
      ['Satellites in use', sats !== null ? fmt(sats, 0) : null],
      ['Accuracy', acc !== null && fix !== false ? `${fmt(acc)} m` : null],
      ['Position', hasPos ? (
        <a
          href={mapLink(lat!, lon!)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-cyan-300 underline-offset-2 hover:underline"
        >
          {fmt(lat!, 4)}, {fmt(lon!, 4)}
          <ArrowTopRightOnSquareIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="sr-only">(open on a map)</span>
        </a>
      ) : null],
    ]} />,
    <Group key="cell" title="Cellular" rows={[
      ['Connection', bool(cell.connected) !== null ? <YesNo value={bool(cell.connected)!} yes="Connected" no="Not connected" /> : null],
      ['Network type', str(cell.type)],
      ['Signal level', num(cell.signal_level) !== null ? fmt(num(cell.signal_level)!, 0) : null],
    ]} />,
    <Group key="bt" title="Bluetooth" rows={[
      ['Bluetooth', bool(bt.enabled) !== null ? <YesNo value={bool(bt.enabled)!} yes="On" no="Off" /> : null],
      ['Paired', num(bt.paired_count) !== null ? fmt(num(bt.paired_count)!, 0) : null],
      ['Connected', num(bt.connected_count) !== null ? fmt(num(bt.connected_count)!, 0) : null],
      ['NMEA data', nmea !== null ? <YesNo value={nmea} yes="Receiving" no="None" /> : null],
    ]} />,
    <Group key="licence" title="Licence" rows={[
      ['Licensing agent', bool(la.connected) !== null ? <YesNo value={bool(la.connected)!} yes="Connected" no="Not connected" warnWhenNo /> : null],
      ['Registered', bool(la.registered) !== null ? <YesNo value={bool(la.registered)!} yes="Yes" no="No" warnWhenNo /> : null],
      ['Plan', str(la.tier)],
      ['Days left', num(la.days_remaining) !== null ? fmt(num(la.days_remaining)!, 0) : null],
    ]} />,
    <Group key="storage" title="Storage and memory" rows={[
      ['Free storage', freeDisk !== null ? megabytes(freeDisk) : null],
      ['Free memory', freeMem !== null ? megabytes(freeMem) : null],
      ['Up for', uptime !== null ? hours(uptime) : null],
    ]} />,
    apps.length > 0 ? (
      <Group key="apps" title="App versions" rows={[]}>
        <ul className="space-y-1.5 text-sm">
          {apps.map((a) => (
            <li key={a.name} className="flex flex-wrap justify-between gap-x-3">
              <span className="min-w-0 break-all text-slate-400">{a.name.replace(/^com\.vortexmarine\./, '')}</span>
              <span className="text-slate-100">{a.version || 'not known'}</span>
            </li>
          ))}
        </ul>
      </Group>
    ) : null,
  ];

  const isEmpty = Object.keys(si).length === 0;
  // Keys this panel knows how to show; anything else is only in the raw snapshot.
  const readable = ['device_model', 'android_version', 'launcher_version', 'screen', 'timezone', 'timestamp', 'battery', 'wifi', 'gps', 'cellular', 'bluetooth', 'nmea_connected', 'la_status', 'free_memory_mb', 'storage_free_mb', 'uptime_hours'].some((k) => si[k] !== undefined && si[k] !== null) || apps.length > 0;
  const versionOf = (pkg: string) => apps.find((a) => a.name === pkg)?.version || null;

  // The facts a report is most often about, one line each.
  const summary = present([
    ['Launcher', str(si.launcher_version) ?? versionOf('com.vortexmarine.launcher')],
    ['VortexNav', versionOf('com.vortexmarine.vortexnav')],
    ['Battery', pct !== null ? `${fmt(pct, 0)}%${charging ? ', charging' : ''}` : null],
    ['GPS', fix !== null ? (
      <YesNo value={fix} yes={sats !== null ? `Fix, ${fmt(sats, 0)} satellites` : 'Has a fix'} no="No fix" warnWhenNo />
    ) : null],
    ['Wi-Fi', wifiOn !== null ? <YesNo value={wifiOn} yes={str(wifi.ssid) ?? 'Connected'} no="Not connected" warnWhenNo /> : null],
    ['Licence', bool(la.registered) !== null ? (
      <YesNo value={bool(la.registered)!} yes={str(la.tier) ?? 'Registered'} no="Not registered" warnWhenNo />
    ) : null],
  ]);

  return (
    <section aria-labelledby="fb-scout-heading" className={className}>
      <h3 id="fb-scout-heading" className="text-[13px] font-semibold uppercase tracking-wider text-slate-400">Scout at the time</h3>
      {sentDate && <p className="mt-0.5 text-sm text-slate-400">{formatDateTime(sentDate)}</p>}
      {isEmpty ? (
        <p className="mt-2 text-sm text-slate-300">No details came with this report.</p>
      ) : (
        <>
          {summary.length > 0 && <div className="mt-3"><Rows rows={summary} /></div>}
          <button
            type="button"
            aria-expanded={all}
            onClick={() => setAll((v) => !v)}
            className="mt-3 inline-flex items-center gap-1 rounded-md text-sm font-medium text-cyan-300 hover:text-cyan-200"
          >
            {all ? 'Fewer details' : 'All details'}
            <ChevronDownIcon className={clsx('h-4 w-4 transition-transform', all && 'rotate-180')} aria-hidden="true" />
          </button>
          {all && (
            <div className="mt-3 space-y-4 border-t border-slate-700/70 pt-4">
              {readable ? groups : <p className="text-sm text-slate-300">Nothing here to lay out. The raw snapshot has what the Scout sent.</p>}
              <div>
                <button
                  type="button"
                  aria-pressed={raw}
                  onClick={() => setRaw((r) => !r)}
                  className="text-sm font-medium text-slate-300 underline-offset-2 hover:text-white hover:underline"
                >
                  {raw ? 'Hide raw snapshot' : 'Show raw snapshot'}
                </button>
                {raw && (
                  <pre className="mt-2 max-h-[420px] overflow-auto whitespace-pre-wrap break-all rounded-lg bg-slate-950 p-3 font-mono text-[13px] leading-relaxed text-slate-200">
                    {JSON.stringify(info, null, 2)}
                  </pre>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}

export default FeedbackSystemInfo;
